/**
 * Trim size of the Canva design parked for this gallery visit, in inches.
 *
 * Deliberately NOT part of {@link GallerySkuFilter}: a design size does not restrict which SKUs
 * come back, it only changes their order. Folding it into the filter would make
 * {@code hasGallerySkuFilter} true for a plain {@code ?canvaDraftId=} visit that carries no tags,
 * which is what {@code CompanyList} uses to decide whether the preloaded SKU list may stand in for
 * a request.
 *
 * Inches because that is what the gallery API ranks in, and the draft endpoint already converts
 * from the PDF's points — nothing in the client handles the 1/72.
 */
export interface GalleryDesignSize {
  designWidthIn?: number;
  designLengthIn?: number;
  /**
   * Pages in the parked design. Sent alongside the size because size alone cannot tell a
   * one-page design apart from the artwork for a coil-bound booklet: that booklet does not fold,
   * so its flat sheet is its finished page and a single page measures as a perfect fit.
   */
  designPageCount?: number;
}

/** A dimension is only usable if it is a finite, positive number, matching the API's own guard. */
const isUsable = (value?: number): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

/**
 * Whether this visit can be ranked by design size. Both dimensions are required: the API skips the
 * whole dimension tier unless it has a complete size, so sending half of one is misleading.
 */
export const hasGalleryDesignSize = (
  designSize?: GalleryDesignSize
): boolean =>
  isUsable(designSize?.designWidthIn) && isUsable(designSize?.designLengthIn);

/** A page count is only usable if it is a whole number of at least one page. */
const isUsablePageCount = (value?: number): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0;

/**
 * Serialises the design size as the query the gallery APIs expect; empty when unusable.
 *
 * <p>The page count rides along with the size rather than standing on its own: it refines which
 * products can carry the design, and without a size there is no matching for it to refine.
 */
export const galleryDesignSizeQuery = (
  designSize?: GalleryDesignSize
): string => {
  if (!hasGalleryDesignSize(designSize)) {
    return '';
  }
  const pages = isUsablePageCount(designSize?.designPageCount)
    ? `&designPageCount=${designSize!.designPageCount}`
    : '';
  return `designWidthIn=${designSize!.designWidthIn}&designLengthIn=${
    designSize!.designLengthIn
  }${pages}`;
};

/**
 * Stable identity for a design size, for use as a React key or effect dependency. The card view
 * pages through ranked results, so a size change has to restart that paging rather than append a
 * page ordered by different rules.
 */
export const galleryDesignSizeKey = (
  designSize?: GalleryDesignSize
): string => galleryDesignSizeQuery(designSize);
